import { useEffect, useCallback, useRef } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import axios from "axios";

const LS_REVIEW_SUBMITTED_KEY = "analyticshero_review_submitted";

function isReviewSubmittedLocally() {
    try {
        return localStorage.getItem(LS_REVIEW_SUBMITTED_KEY) === "true";
    } catch {
        return false;
    }
}

function markReviewSubmittedLocally() {
    try {
        localStorage.setItem(LS_REVIEW_SUBMITTED_KEY, "true");
    } catch { }
}

async function recordPromptOnServer(shopOrigin, type) {
    try {
        await axios.post("/analytics/record-review-prompt/", {
            shop: shopOrigin,
            type,
        });
    } catch (err) {
        console.error("[ReviewManager] Failed to record prompt:", err);
    }
}

async function markSubmittedOnServer(shopOrigin) {
    try {
        await axios.post("/analytics/mark-review-submitted/", {
            shop: shopOrigin,
        });
    } catch (err) {
        console.error("[ReviewManager] Failed to mark submitted:", err);
    }
}

async function fetchReviewSubmittedFromServer(shopOrigin) {
    try {
        const res = await axios.get(
            `/analytics/get-review-status/?shop=${shopOrigin}`
        );
        return !!res.data.review_submitted;
    } catch {
        return false;
    }
}

async function requestReviewModal(shopify, shopOrigin, type) {
    console.log("[DEBUG] shopify object keys:", shopify ? Object.keys(shopify) : "shopify is null/undefined");
    console.log("[DEBUG] shopify.reviews:", shopify?.reviews);
    try {
        const result = await shopify.reviews.request();

        if (result.success && result.code === "success") {
            recordPromptOnServer(shopOrigin, type);
            return { success: true, code: "success" };
        }

        if (!result.success && result.code === "already-reviewed") {
            markReviewSubmittedLocally();
            markSubmittedOnServer(shopOrigin);
            return { success: false, code: "already-reviewed" };
        }

        return { success: false, code: result.code, message: result.message };
    } catch (error) {
        console.error("[ReviewManager] shopify.reviews.request() threw:", error);
        window.open(
            "https://apps.shopify.com/analyticshero#modal-show=WriteReviewModal",
            "_blank"
        );
        return { success: false, code: "fallback", fallback: true };
    }
}

export default function ReviewManager({ shopOrigin, onTriggerAvailable }) {
    console.log("[ReviewManager] MOUNTED, shopOrigin:", shopOrigin);
    const shopify = useAppBridge();
    const hasAutoTriggered = useRef(false);

    const triggerReviewModal = useCallback(
        async (type = "manual") => {
            try {
                if (type === "manual") {
                    return await requestReviewModal(shopify, shopOrigin, type);
                }

                if (isReviewSubmittedLocally()) {
                    return { success: false, code: "review_already_submitted" };
                }

                const submittedOnServer = await fetchReviewSubmittedFromServer(shopOrigin);
                if (submittedOnServer) {
                    markReviewSubmittedLocally();
                    return { success: false, code: "review_already_submitted" };
                }

                return await requestReviewModal(shopify, shopOrigin, type);
            } catch (error) {
                console.error("[ReviewManager] triggerReviewModal error:", error);
                return { success: false, code: "error", message: error.message };
            }
        },
        [shopOrigin, shopify]
    );

    useEffect(() => {
        if (!shopOrigin || hasAutoTriggered.current) return;

        const checkAndTrigger = async () => {
            try {
                hasAutoTriggered.current = true;

                if (isReviewSubmittedLocally()) return;

                try {
                    const response = await axios.get(
                        `/analytics/get-review-eligibility/?shop=${shopOrigin}&type=automatic`
                    );
                    const { eligible, reason } = response.data;
                    if (!eligible) {
                        if (reason === "review_already_submitted") {
                            markReviewSubmittedLocally();
                        }
                        return;
                    }
                } catch (err) {
                    console.error("[ReviewManager] Eligibility check failed:", err);
                }

                setTimeout(async () => {
                    await triggerReviewModal("automatic");
                }, 2000);
            } catch (error) {
                console.error("[ReviewManager] Auto error:", error);
            }
        };

        checkAndTrigger();
    }, [shopOrigin, triggerReviewModal]);

    useEffect(() => {
        if (onTriggerAvailable) {
            onTriggerAvailable(triggerReviewModal);
        }
    }, [triggerReviewModal, onTriggerAvailable]);

    return null;
}

export function useReviewTrigger(shopOrigin) {
    const shopify = useAppBridge();

    const triggerReview = useCallback(async () => {
        try {
            return await requestReviewModal(shopify, shopOrigin, "manual");
        } catch (error) {
            console.error("[useReviewTrigger] Error:", error);
            return { success: false, code: "error", message: error.message };
        }
    }, [shopOrigin, shopify]);

    return triggerReview;
}